/**
 * #564: where an approved plan will actually run, said exactly.
 *
 * The server pins each task at approval (decide_plan): the Worker a drafted task names,
 * else the Worker the owner's command addresses ("4 run ..."), else the project's
 * standing routing scope, else any idle Worker. A drafted task that disagrees with
 * the owner's address is refused (worker_target_conflict); a retired Worker cannot be
 * addressed (worker_retired). The tick then waits for exactly that Worker and its
 * exact pinned runtime - it never falls back to the first free Worker and never
 * substitutes a runtime (pinned_offering_unavailable).
 *
 * This module says the same thing before approval, from the same rules. Only Workers
 * the #562 projection counts as usable count as "free"; offline and historical
 * Workers never do.
 */
import { isActiveUsableWorker } from './active-worker-projection';
import type {
  ModelOfferingRef, NodeOffering, NodePresence, Plan, Worker, WorkerOfferingPreference, WorkforceSession,
} from './data';
import type { WorkerChatReadiness } from './worker-chat-readiness';
import { leadingWorkerNumber as addressedWorkerNumber, namedWorkerNumbers } from './routing-view';

export interface WorkerRouteTarget {
  readonly workerNumber: number;
  readonly workerName: string;
  /** Online and addressable now, per the #562 active projection. */
  readonly usable: boolean;
  readonly retired: boolean;
  readonly reason: string | null;
  readonly nodeName: string | null;
  readonly runtimeLabel: string | null;
  readonly modelLabel: string | null;
  /** True when the Worker has an exact offering pin on its computer. */
  readonly pinned: boolean;
  /** False when that pinned offering is no longer on its computer (dispatch blocks). */
  readonly pinnedOfferingOnNode: boolean;
}

export interface RoutingScope {
  readonly project_id: string;
  readonly worker_number: number;
}

/** The same reading as brunel_private.leading_worker_number: one twin, kept in routing-view. */
export function leadingWorkerNumber(content: string | null | undefined): number | null {
  return addressedWorkerNumber(content ?? '');
}

function modelLabelFor(offering: ModelOfferingRef | null): string | null {
  if (offering === null) return null;
  return offering.model_key === offering.runtime_key ? 'CLI default model' : offering.model_key;
}

export function workerRouteTargets(input: {
  readonly readiness: readonly WorkerChatReadiness[];
  readonly preferences: readonly WorkerOfferingPreference[];
  readonly nodeOfferings: readonly NodeOffering[];
  readonly modelOfferings: readonly ModelOfferingRef[];
}): readonly WorkerRouteTarget[] {
  return input.readiness.map((worker) => {
    const preference = worker.nodeId === null ? null
      : input.preferences.find((row) => row.worker_id === worker.workerId && row.node_id === worker.nodeId) ?? null;
    const offeringKey = preference?.offering_key ?? worker.offeringKey;
    const offering = input.modelOfferings.find((row) => row.key === offeringKey) ?? null;
    const pinnedOfferingOnNode = preference === null || worker.nodeId === null
      ? true
      : input.nodeOfferings.some((row) => row.node_id === worker.nodeId && row.offering_key === preference.offering_key);
    return {
      workerNumber: worker.workerNumber,
      workerName: worker.workerName,
      usable: isActiveUsableWorker(worker),
      retired: worker.availability === 'retired',
      reason: worker.reasonText,
      nodeName: worker.nodeName,
      runtimeLabel: offering?.runtime_key ?? worker.runtimeLabel ?? worker.runtimeKey,
      modelLabel: modelLabelFor(offering) ?? worker.modelKey,
      pinned: preference !== null,
      pinnedOfferingOnNode,
    };
  });
}

/**
 * One Worker's open session in one project, and the AIs Brunel would run for it on that
 * computer if the owner chose that Worker for a held plan.
 *
 * The server's check of a chosen Worker (owner_command_readiness_for, one named AI) takes the
 * AI the Worker is set to use there, or else the AI its computer has exactly one signed-in
 * setup of; a metered setup never counts. This says the same from the rows on screen and
 * leaves out what they cannot settle. The server still decides when a Worker is chosen.
 */
export interface WorkerSeat {
  readonly workerNumber: number;
  readonly workerName: string;
  readonly projectId: string;
  readonly nodeName: string | null;
  readonly runtimeKeys: readonly string[];
}

const OPEN_SESSION: ReadonlySet<string> = new Set(['active', 'restored']);

export function workerSeats(input: {
  readonly workers: readonly Worker[];
  readonly sessions: readonly WorkforceSession[];
  readonly nodes: readonly NodePresence[];
  readonly preferences: readonly WorkerOfferingPreference[];
  readonly nodeOfferings: readonly NodeOffering[];
  readonly modelOfferings: readonly ModelOfferingRef[];
}): readonly WorkerSeat[] {
  const seats: WorkerSeat[] = [];
  for (const worker of input.workers) {
    if (worker.retired_at) continue;
    const open = input.sessions.filter((session) => session.worker_id === worker.id && OPEN_SESSION.has(session.lifecycle));
    for (const projectId of new Set(open.map((session) => session.project_id))) {
      const nodeIds = [...new Set(open.filter((session) => session.project_id === projectId).map((session) => session.node_id))];
      // Open sessions on two computers: which one the server reads is not this screen's to guess.
      if (nodeIds.length !== 1) continue;
      const nodeId = nodeIds[0]!;
      const onNode = new Set(input.nodeOfferings.filter((row) => row.node_id === nodeId).map((row) => row.offering_key));
      const local = input.modelOfferings.filter((offering) => onNode.has(offering.key) && offering.execution_location !== 'cloud');
      const setTo = input.preferences.find((row) => row.worker_id === worker.id && row.node_id === nodeId)?.offering_key ?? null;
      const runtimeKeys = [...new Set(local.map((offering) => offering.runtime_key))].filter((runtime) => {
        const setups = local.filter((offering) => offering.runtime_key === runtime);
        return setups.length === 1 || setups.some((offering) => offering.key === setTo);
      });
      seats.push({
        workerNumber: worker.number,
        workerName: worker.name,
        projectId,
        nodeName: input.nodes.find((node) => node.node_id === nodeId)?.name ?? null,
        runtimeKeys,
      });
    }
  }
  return seats.sort((a, b) => a.workerNumber - b.workerNumber);
}

export interface PlanRouting {
  /** One sentence for the approval card. */
  readonly text: string;
  /** Approval will be refused, or the work cannot start as pinned. Nothing is rerouted. */
  readonly blocked: boolean;
  /** The single exact Worker every task goes to, when there is one. */
  readonly workerNumber: number | null;
}

function describe(target: WorkerRouteTarget): string {
  const parts = [`Worker ${target.workerNumber}`];
  if (target.nodeName !== null) parts.push(target.nodeName);
  const ai = [target.runtimeLabel, target.modelLabel].filter((part): part is string => part !== null && part !== '').join(' · ');
  if (ai !== '') parts.push(ai + (target.pinned ? ' (pinned)' : ' (Auto)'));
  return parts.join(' · ');
}

function exactTarget(number: number, targets: readonly WorkerRouteTarget[]): PlanRouting {
  const target = targets.find((entry) => entry.workerNumber === number) ?? null;
  if (target === null) {
    return { text: `Blocked: Worker ${number} is not a Worker here. Approval is refused; nothing is rerouted.`, blocked: true, workerNumber: number };
  }
  if (target.retired) {
    return { text: `Blocked: Worker ${number} is retired. Approval is refused; nothing is rerouted.`, blocked: true, workerNumber: number };
  }
  if (target.pinned && !target.pinnedOfferingOnNode) {
    return {
      text: `Runs only on ${describe(target)} — blocked: that pinned runtime is not on its computer. Brunel will not substitute another runtime or Worker.`,
      blocked: true,
      workerNumber: number,
    };
  }
  if (!target.usable) {
    return {
      text: `Runs only on ${describe(target)} — not available now${target.reason ? ` (${target.reason})` : ''}. It waits for Worker ${number}; no other Worker is used.`,
      blocked: false,
      workerNumber: number,
    };
  }
  return { text: `Runs only on ${describe(target)}.`, blocked: false, workerNumber: number };
}

/**
 * Where this plan's tasks will run once approved, by the server's own precedence.
 * Returns null when the caller has no route targets to read (an unknown is left unsaid).
 */
export function planRouting(input: {
  readonly plan: Plan;
  readonly commandContent: string | null;
  readonly targets: readonly WorkerRouteTarget[];
  readonly scopes: readonly RoutingScope[];
  readonly runningWorkerNumbers: ReadonlySet<number>;
}): PlanRouting {
  const addressed = leadingWorkerNumber(input.commandContent);
  const tasks = Array.isArray(input.plan.tasks) ? input.plan.tasks : [];
  const named = [...new Set(tasks
    .map((task) => (typeof task.preferredWorkerNumber === 'number' && task.preferredWorkerNumber > 0 ? task.preferredWorkerNumber : null))
    .filter((n): n is number => n !== null))];
  // Another Worker the owner's own words also name is not a contradiction: the server keeps
  // each task with the Worker the owner named for it.
  const ownerNamed = namedWorkerNumbers(input.commandContent ?? '');
  const conflicting = addressed === null ? [] : named.filter((n) => n !== addressed && !ownerNamed.includes(n));
  if (conflicting.length > 0) {
    return {
      text: `Blocked: you addressed Worker ${addressed}, but the drafted plan names Worker ${conflicting.join(', ')}. Approval is refused; nothing is rerouted.`,
      blocked: true,
      workerNumber: null,
    };
  }
  const scope = input.scopes.find((row) => row.project_id === input.plan.project_id)?.worker_number ?? null;
  const perTask = tasks.length === 0 ? [named[0] ?? addressed ?? scope] : tasks.map((task) =>
    (typeof task.preferredWorkerNumber === 'number' && task.preferredWorkerNumber > 0 ? task.preferredWorkerNumber : null) ?? addressed ?? scope);
  const pinned = [...new Set(perTask.filter((n): n is number => n !== null))];
  const unpinned = perTask.some((n) => n === null);

  if (!unpinned && pinned.length === 1) return exactTarget(pinned[0]!, input.targets);
  if (!unpinned && pinned.length > 1) {
    const each = pinned.map((n) => exactTarget(n, input.targets));
    return {
      text: `Runs on the Worker each task names: ${each.map((entry) => entry.text).join(' ')}`,
      blocked: each.some((entry) => entry.blocked),
      workerNumber: null,
    };
  }
  const free = input.targets
    .filter((target) => target.usable && !input.runningWorkerNumbers.has(target.workerNumber))
    .map((target) => target.workerNumber)
    .sort((a, b) => a - b);
  const anyone = input.targets.some((target) => target.usable)
    ? free.length === 0
      ? 'it waits for an active Worker to come free'
      : `the first free active Worker (${free.length === 1 ? `Worker ${free[0]}` : `Worker ${free[0]}, of ${free.length} free`})`
    : 'no active Worker is online';
  if (pinned.length === 0) return { text: `Runs on ${anyone}.`, blocked: false, workerNumber: null };
  return {
    text: `Some tasks are pinned (${pinned.map((n) => exactTarget(n, input.targets).text).join(' ')}); the rest run on ${anyone}.`,
    blocked: false,
    workerNumber: null,
  };
}

/** Exactly what Settings -> AI Workforce -> "Smoke test selected AI" sends: addressed, read-only. */
export function smokeTestCommand(workerNumber: number): string {
  return `${workerNumber} run a read-only AI smoke test. Report the current working directory, git branch, whether the working tree is clean, and the latest commit hash. Do not edit files, do not commit, do not push, and do not install anything.`;
}
